/**
 * Shared preprocessor between React (preview) and backend (PDF)
 * Takes `previewData` (the raw invoice config) and outputs
 * a fully prepared object ready for Handlebars or HTML rendering.
 *
 * Supports product-level tax configuration via taxComponents array
 *
 * FIXED:
 * - taxComponents[].amount is now computed (it may not exist in input)
 * - NEW param: taxIncluded (boolean)
 *   - false => tax is added on top of line totals
 *   - true  => line total already includes product tax; base is back-calculated and split by qty
 *
 * NEW:
 * - currency formatting with commas:
 *   - INR (₹ / Rs / INR / Rupee) => Indian commas (en-IN)
 *   - others => International commas (en-US)
 */

function formatNumberWithCommas(n, locale = "en-US") {
  const num = Number(n) || 0;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function currencyFmt(n, currency = "") {
  const c = String(currency || "").toLowerCase();
  if (!n) return
  const isINR =
    c.includes("₹") ||
    c.includes("rs") ||
    c.includes("inr") ||
    c.includes("rupee") ||
    c.includes("rupees");

  const locale = isINR ? "en-IN" : "en-US";
  const formatted = formatNumberWithCommas(n, locale);

  return `${currency} ${formatted}`.trim();
}

function normalizeRate(r) {
  const n = Number(r);
  if (Number.isNaN(n)) return 0;
  return Math.abs(n) > 1 ? Math.abs(n) / 100 : Math.abs(n);
}

function preprocessInvoice(previewData = {}, currency = "") {
  const {
    companyName = "Company Name",
    companyAddress = "Address Line 1\nAddress Line 2",
    companyLogo,
    discounts: rawDiscountsCorrect,
    serviceAddress,
    duePeriod: topLevelDuePeriod,
    themeColor = "#69B345",
    textColor = "#ffffff",
    taxes: rawTaxes,
    lineItems: rawLineItems,
    invoice = {},
    recipient = {},
    notes,
    footer,
    showPageNumber = true,
    invoiceTitle = "Tax Invoice",

    // NEW
    taxIncluded = false
  } = previewData || {};

  /** Discounts */
  const rawDiscounts = Array.isArray(rawDiscountsCorrect) ? rawDiscountsCorrect : [];

  /** Dates */
  const today = new Date();
  const issuedAt = today.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  const effectiveDueDays = Number(invoice?.duePeriod ?? topLevelDuePeriod ?? 15);
  const dueDate = new Date(today);
  dueDate.setDate(today.getDate() + (Number.isFinite(effectiveDueDays) ? effectiveDueDays : 15));
  const dueAt = dueDate.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  /** Line Items */
  const itemsSource =
    Array.isArray(rawLineItems) && rawLineItems.length > 0
      ? rawLineItems
      : [
          {
            id: "1",
            name: "Website Design",
            description: "Landing page + responsive UI",
            qty: 1,
            unitPrice: 1200,
            taxComponents: [
              { name: "VAT", rate: "8" },
              { name: "Service Tax", rate: "5" }
            ]
          },
          {
            id: "2",
            name: "SEO Audit",
            description: "Technical SEO & recommendations",
            qty: 1,
            unitPrice: 400,
            taxComponents: [{ name: "VAT", rate: "8" }]
          },
        ];

  /** Product-level tax summary accumulator */
  const taxSummaryMap = {};

  const items = itemsSource.map((it, idx) => {
    const qty = Number(it.qty ?? 0) || 0;

    /** Determine line total */
    let lineTotal;
    if (it.totalPrice != null && it.totalPrice !== "") {
      lineTotal = Number(it.totalPrice) || 0;
    } else {
      const unitPriceInput = Number(it.unitPrice ?? it.price ?? 0) || 0;
      lineTotal = qty * unitPriceInput;
    }

    /** Normalize tax components */
    const rawTaxComponents = Array.isArray(it.taxComponents) ? it.taxComponents : [];
    const normalizedTaxComponents = rawTaxComponents.map((t) => {
      const ratePercent = Number(t?.rate ?? 0) || 0;
      const rateDecimal = normalizeRate(ratePercent);
      return {
        name: t?.name || "Tax",
        ratePercent: rateDecimal * 100,
        rateDecimal,
      };
    });

    const totalTaxRateDecimal = normalizedTaxComponents.reduce((s, t) => s + t.rateDecimal, 0);

    /**
     * Base line total (pre-tax)
     * - if taxIncluded=true: back-calc base from final total
     * - if taxIncluded=false: base is the lineTotal itself
     */
    const baseLineTotal = taxIncluded
      ? (totalTaxRateDecimal > 0 ? lineTotal / (1 + totalTaxRateDecimal) : lineTotal)
      : lineTotal;

    /** Unit price should be based on baseLineTotal (split across qty) */
    const unitPriceBase = qty > 0 ? baseLineTotal / qty : 0;

    /** Compute taxes per component */
    const taxes = [];
    let itemTaxTotal = 0;

    normalizedTaxComponents.forEach((t) => {
      const amount = baseLineTotal * t.rateDecimal;

      taxes.push({
        name: t.name,
        rate: Number(t.ratePercent.toFixed(2)),
        amount,
        amountFmt: currencyFmt(amount, currency),
      });

      itemTaxTotal += amount;

      const taxKey = t.name || "Tax";
      if (!taxSummaryMap[taxKey]) {
        taxSummaryMap[taxKey] = {
          name: taxKey,
          totalAmount: 0,
          rates: new Set()
        };
      }
      taxSummaryMap[taxKey].totalAmount += amount;
      taxSummaryMap[taxKey].rates.add(Number(t.ratePercent.toFixed(2)));
    });

    /**
     * Final line total:
     * - taxIncluded=true  => keep lineTotal as-is (already includes product tax)
     * - taxIncluded=false => base + product taxes
     */
    const finalTotal = taxIncluded ? lineTotal : baseLineTotal + itemTaxTotal;

    return {
      id: it.id ?? `item-${idx}`,
      name: it.name ?? `Item ${idx + 1}`,
      description: it.description ?? "",
      qty,

      unitPrice: Number(unitPriceBase.toFixed(2)),
      unitPriceFmt: currencyFmt(unitPriceBase, currency),

      total: Number(finalTotal.toFixed(2)),
      totalFmt: currencyFmt(finalTotal, currency),

      // used by template: total before tax
      baseTotal: Number(baseLineTotal.toFixed(2)),
      baseTotalFmt: currencyFmt(baseLineTotal, currency),

      taxes: taxes.length > 0 ? taxes : null,
      itemTaxTotal: Number(itemTaxTotal.toFixed(2)),
    };
  });

  /**
   * Subtotal:
   * Always show what the customer pays for product lines
   */
  const subtotal = items.reduce((s, it) => s + (it.total || 0), 0);
  const subtotalFmt = currencyFmt(subtotal, currency);

  /** Base subtotal (pre-product-tax) used for legacy/global taxes */
  const baseSubtotal = items.reduce((s, it) => s + (it.baseTotal || 0), 0);

  /** Discounts (applied on subtotal shown) */
  const discounts = rawDiscounts.map((d) => {
    const percent = Number(d?.value ?? 0);
    const amount = subtotal * (percent / 100);
    return { name: d?.name ?? "Discount", percent, amount, amountFmt: currencyFmt(amount, currency) };
  });

  const totalDiscount = discounts.reduce((s, d) => s + (d.amount || 0), 0);
  const totalDiscountFmt = currencyFmt(totalDiscount, currency);
  const totalDiscountPercent = subtotal ? ((totalDiscount / subtotal) * 100).toFixed(2) : "0.00";
  const hasDiscounts = discounts.length > 0 && totalDiscount > 0;

  /** Product-level tax summary */
  const taxSummary = Object.values(taxSummaryMap).map((tax) => {
    const ratesArray = Array.from(tax.rates);
    const totalRate = ratesArray.length === 1
      ? ratesArray[0]
      : ratesArray.reduce((a, b) => a + b, 0);

    return {
      name: tax.name,
      totalRate: Number(totalRate).toFixed(2),
      totalAmount: tax.totalAmount,
      totalAmountFmt: currencyFmt(tax.totalAmount, currency)
    };
  });

  const productTaxTotal = items.reduce((s, it) => s + (it.itemTaxTotal || 0), 0);

  /** Legacy global taxes */
  const taxGroupsObj = rawTaxes && typeof rawTaxes === "object" && !Array.isArray(rawTaxes) ? rawTaxes : {};

  /**
   * Taxable base for legacy taxes:
   * Always apply legacy tax on baseSubtotal (pre-product-tax) minus discount
   */
  const taxableBase = Math.max(0, baseSubtotal - totalDiscount);

  const taxGroupResults = Object.keys(taxGroupsObj).map((key) => {
    const group = taxGroupsObj[key] || {};
    const members = Array.isArray(group.items) ? group.items : Object.values(group.items || {});
    const memberRows = members.map((m) => {
      const rate = normalizeRate(m.value ?? 0);
      const amount = taxableBase * rate;
      return {
        name: m.name,
        rawRate: m.value,
        rateDecimal: rate,
        amount,
        amountFmt: currencyFmt(amount, currency),
      };
    });
    const accumulatedPercent = memberRows.reduce((s, m) => s + m.rateDecimal * 100, 0);
    const groupAmount = memberRows.reduce((s, m) => s + m.amount, 0);
    return {
      name: group.name ?? key,
      members: memberRows,
      accumulatedPercent: Number(accumulatedPercent.toFixed(2)),
      groupAmount,
      groupAmountFmt: currencyFmt(groupAmount, currency),
    };
  });

  const legacyTaxTotal = taxGroupResults.reduce((s, g) => s + g.groupAmount, 0);

  /** Total tax */
  const taxTotal = productTaxTotal + legacyTaxTotal;
  const taxTotalFmt = currencyFmt(taxTotal, currency);

  /**
   * Grand total:
   * - subtotal already includes product tax if taxIncluded=true
   * - subtotal includes product tax (because we added it) if taxIncluded=false
   * - legacy taxes are always added here
   */
  const grandTotal = subtotal - totalDiscount + legacyTaxTotal;
  const grandTotalFmt = currencyFmt(grandTotal, currency);

  /** Addresses */
  const serviceAddressDisplay =
    typeof serviceAddress === "string"
      ? serviceAddress
      : (serviceAddress && (serviceAddress.address || serviceAddress.street)) || "";

  /** Final view-model */
  return {
    companyName,
    companyAddress,
    companyLogo,

    recipient: {
      name: recipient?.name || "Client Name",
      address: recipient?.address || "Address line 1\nCity, State ZIP",
      phone: recipient?.phone || "",
    },

    serviceAddressDisplay: serviceAddressDisplay || "Address line 1\nCity, State ZIP",

    invoice: {
      number: invoice?.number ?? "000",
      title: invoice?.title || "Services",
    },

    issuedAt,
    dueAt,
    themeColor,
    textColor,
    invoiceTitle,

    taxIncluded: Boolean(taxIncluded),

    items,

    subtotalFmt,

    discounts,
    hasDiscounts,
    totalDiscountFmt,
    totalDiscountPercent,

    taxSummary: taxSummary.length > 0 ? taxSummary : null,

    taxGroupResults,
    taxTotalFmt,

    grandTotalFmt,

    notes: notes || invoice?.message || "",
    footer: footer || "",
    showPageNumber: Boolean(previewData?.showPageNumber ?? true),

    currency,
  };
}


export { formatNumberWithCommas,currencyFmt,normalizeRate,preprocessInvoice };
